import React, { useEffect, useState } from 'react';
import {
  Button,
  CompositeView,
  FilterWidget,
  GridView,
  SimpleView,
  Widget,
  notification,
  useRouter,
} from '@digital/ui';
import { Checkbox, DatePicker, Input, Modal, Select, Spin, Table, Tag, Upload } from 'antd';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { SearchButton } from '@app/components/FormButtons';
import { PaginationResult } from '@app/api/pagination';
import {
  AtivacaoContratoResumo,
  AtivacaoContratoStatus,
  MinutaTesteExtracaoCampo,
  MinutaTesteExtracaoResultado,
  PqTesteValidacaoResultado,
  consultarAtivacoesContrato,
  testarExtracaoMinuta,
  testarValidacaoPq,
  executarHandoverTeste,
  AtivacaoContratoTesteUploadResultado,
  verificarHandoverTesteDisponivel,
  verificarTesteExtracaoMinutaDisponivel,
  verificarTestePqDisponivel,
} from '@app/api/ativacaoContrato';
import './styles.css';

const STATUS_OPCOES: { value: AtivacaoContratoStatus; label: string }[] = [
  { value: 'Pendente', label: 'Aguardando processamento' },
  { value: 'EmProcessamento', label: 'Em processamento' },
  { value: 'Sucesso', label: 'Contrato ativado' },
  { value: 'Divergencia', label: 'Divergência' },
  { value: 'PendenteAcaoManual', label: 'Pendente' },
  { value: 'ErroTecnico', label: 'Erro técnico' },
  { value: 'NaoProcessado', label: 'Não processado' },
  { value: 'AguardandoAtivacao', label: 'Aguardando ativação' },
  { value: 'AtivadoManualmente', label: 'Ativado manualmente' },
];

const CAMPOS_LABEL: { [key: string]: string } = {
  NumeroContrato: 'Número do contrato',
  DataInicio: 'Data de início',
  DataTermino: 'Data de término',
  ValorTotal: 'Valor total do contrato',
  CnpjContratada: 'CNPJ da contratada',
  NomeContratada: 'Nome da contratada',
  Objeto: 'Objeto do contrato',
  Localidade: 'Localidade',
  LocalidadePtr: 'Localidade do PTR',
  CondicaoPagamentoDias: 'Prazo de pagamento',
  Gestor: 'Gestor MRN',
  EmailGestor: 'E-mail do gestor MRN',
  ResponsavelContratada: 'Responsável da contratada',
  EmailResponsavel: 'E-mail do responsável da contratada',
  UsuarioGestorId: 'Gestor MRN',
  FiscalId: 'Fiscal do contrato',
  ResponsavelRateioId: 'Responsável pelo rateio',
  ResponsavelFornecedorNome: 'Responsável da contratada',
  ResponsavelFornecedorEmail: 'E-mail do responsável da contratada',
  ResponsavelFornecedorEmailFinanceiro: 'E-mail financeiro da contratada',
  DataLimiteInserirMedicao: 'Prazo para apresentação da medição',
  DataLimiteValidarMedicao: 'Prazo para aprovação da medição',
  DataLimiteReapresentarMedicao: 'Prazo para reapresentação da medição',
  DataLimiteReavaliarMedicao: 'Prazo para reavaliação da medição',
  DataLimiteAutorizarFaturamento: 'Prazo para autorização do faturamento',
  DataMensalLimiteInserirMedicao: 'Apresentação do BM pelo fornecedor',
  DataMensalLimiteValidarMedicao: 'Aprovação do BM pela MRN',
  DataMensalLimiteReapresentarMedicao: 'Reapresentação do BM pelo fornecedor',
  DataMensalLimiteReavaliarMedicao: 'Reavaliação do BM pela MRN',
  DataMensalLimiteAutorizarFaturamento: 'Autorização de faturamento',
  DataMensalLimiteInserirMedicaoAplicavel: 'Apresentação do BM pelo fornecedor — aplicável',
  DataMensalLimiteValidarMedicaoAplicavel: 'Aprovação do BM pela MRN — aplicável',
  Reajuste: 'Reajuste contratual',
  ReajusteDataBase: 'Data-base do reajuste',
  ReajusteFrequencia: 'Frequência do reajuste',
  ReajusteIndice: 'Índice de reajuste',
  SegurosGarantias: 'Seguros e garantias',
  PossuiSegurosGarantias: 'Possui seguros e garantias',
  PossuiAnexoTratamentoDadosPessoais: 'Anexo de tratamento de dados pessoais',
  PossuiSlaMonitorado: 'SLA monitorado',
  AprovacaoPagamentoAutomatica: 'Documentos de cobrança automáticos',
  UtilizaProcessoRateio: 'Possui processo de rateio',
  PossuiDocumentacaoAcessoria: 'Possui documentação acessória',
  Municipios: 'Municípios do contrato',
  Municipio: 'Município',
  Anexos: 'Anexos do contrato',
  Item: 'Item da planilha',
  Quantidade: 'Quantidade',
  ValorUnitario: 'Valor unitário',
  ValorAcumulado: 'Valor acumulado',
  TotalizadorValorAcumulado: 'Total acumulado',
  MaoDeObraAlocadaPTR: 'Mão de obra alocada no PTR',
  MaoDeObraAlocadaForaPTR: 'Mão de obra alocada fora do PTR',
};

const statusExecucaoLabel = (status: string | null) => {
  const opcao = STATUS_OPCOES.find(x => x.value === status);
  if (opcao) return opcao.label;
  if (status === 'NaoExecutado') return 'Não executado';
  return status || '-';
};

const formatarData = (valor: string | null) => (valor ? new Date(valor).toLocaleString('pt-BR') : '-');

const ativadoPorLabel = (valor: string | null) => {
  if (valor === 'ATIVACAO_AUTOMATICA') return 'Ativação automática';
  return valor || '-';
};

const formatarMoeda = (valor: number | null) =>
  valor == null ? '-' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const obterValidacoesTestePq = (resultado: PqTesteValidacaoResultado) => {
  if (resultado.erros && resultado.erros.length > 0) {
    return resultado.erros;
  }

  return [
    {
      codigo: 'PQ_VALIDADA',
      linha: null,
      mensagem: `Nenhum erro encontrado nas ${resultado.quantidadeLinhas} linhas processadas.`,
    },
  ];
};

const formatarNomeTecnico = (valor: string) => {
  const texto = valor
    .replace(/_/g, ' ')
    .replace(/([a-zá-ú0-9])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const campoLabel = (campo: string | null) => {
  if (!campo) return null;
  return CAMPOS_LABEL[campo] || formatarNomeTecnico(campo);
};

const metodoExtracaoLabel = (metodo: string | null) => {
  if (metodo === 'Catalogo') return 'Regex do catálogo';
  if (metodo === 'RegexLocal') return 'Regex local';
  return metodo ? formatarNomeTecnico(metodo) : '-';
};

const possuiValor = (valor: string | null | undefined) =>
  typeof valor === 'string' && valor.trim() !== '' && valor.trim() !== '-';

const numeroQuery = (params: URLSearchParams, chave: string, fallback?: number) => {
  const valor = params.get(chave);
  if (!valor) return fallback;
  const numero = Number(valor);
  return Number.isFinite(numero) && numero > 0 ? numero : fallback;
};

const statusQuery = (params: URLSearchParams) => {
  const valor = params.get('status') as AtivacaoContratoStatus | null;
  return valor && STATUS_OPCOES.some(opcao => opcao.value === valor) ? valor : undefined;
};

const AtivacaoContrato = () => {
  const { history, location } = useRouter();
  const parametrosIniciais = new URLSearchParams(location.search);
  const [paginacao, setPaginacao] = useState<PaginationResult<AtivacaoContratoResumo> | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageAtual, setPageAtual] = useState(numeroQuery(parametrosIniciais, 'pagina', 1) as number);
  const [sizeAtual, setSizeAtual] = useState(numeroQuery(parametrosIniciais, 'tamanho', 20) as number);
  const [filtroContrato, setFiltroContrato] = useState<number | undefined>(numeroQuery(parametrosIniciais, 'contrato'));
  const [filtroStatus, setFiltroStatus] = useState<AtivacaoContratoStatus | undefined>(statusQuery(parametrosIniciais));
  const [filtroCriadoDe, setFiltroCriadoDe] = useState<string | undefined>(parametrosIniciais.get('de') || undefined);
  const [filtroCriadoAte, setFiltroCriadoAte] = useState<string | undefined>(
    parametrosIniciais.get('ate') || undefined,
  );
  const [testeDisponivel, setTesteDisponivel] = useState(false);
  const [testeVisivel, setTesteVisivel] = useState(false);
  const [testeEnviando, setTesteEnviando] = useState(false);
  const [testeMinuta, setTesteMinuta] = useState<any>(null);
  const [testeResultado, setTesteResultado] = useState<MinutaTesteExtracaoResultado | null>(null);
  const [testePqDisponivel, setTestePqDisponivel] = useState(false);
  const [testePqVisivel, setTestePqVisivel] = useState(false);
  const [testePqEnviando, setTestePqEnviando] = useState(false);
  const [testePqArquivo, setTestePqArquivo] = useState<any>(null);
  const [testePqValorTotal, setTestePqValorTotal] = useState('');
  const [testePqResultado, setTestePqResultado] = useState<PqTesteValidacaoResultado | null>(null);
  const [handoverTesteDisponivel, setHandoverTesteDisponivel] = useState(false);
  const [handoverTesteVisivel, setHandoverTesteVisivel] = useState(false);
  const [handoverTesteEnviando, setHandoverTesteEnviando] = useState(false);
  const [handoverTesteContrato, setHandoverTesteContrato] = useState('');
  const [handoverTesteMinuta, setHandoverTesteMinuta] = useState<any>(null);
  const [handoverTestePq, setHandoverTestePq] = useState<any>(null);
  const [handoverTesteMultiplosGestores, setHandoverTesteMultiplosGestores] = useState(false);
  const [handoverTesteFaturamentoDireto, setHandoverTesteFaturamentoDireto] = useState(false);
  const [handoverTesteAdiantamentoFinanceiro, setHandoverTesteAdiantamentoFinanceiro] = useState(false);
  const [handoverTesteResultado, setHandoverTesteResultado] = useState<AtivacaoContratoTesteUploadResultado | null>(
    null,
  );

  const buscar = (pagina: number, tamanhoPagina: number) => {
    setLoading(true);
    consultarAtivacoesContrato({
      contratoNumero: filtroContrato,
      criadoDe: filtroCriadoDe,
      criadoAte: filtroCriadoAte,
      status: filtroStatus,
      pagina,
      tamanhoPagina,
    })
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        if (data) setPaginacao(data);
      })
      .finally(() => setLoading(false));
  };

  const atualizarUrl = (
    pagina: number,
    tamanhoPagina: number,
    filtros?: {
      contrato?: number;
      status?: AtivacaoContratoStatus;
      criadoDe?: string;
      criadoAte?: string;
    },
  ) => {
    const parametros = new URLSearchParams();
    const valores = filtros || {
      contrato: filtroContrato,
      status: filtroStatus,
      criadoDe: filtroCriadoDe,
      criadoAte: filtroCriadoAte,
    };
    if (valores.contrato != null) parametros.set('contrato', String(valores.contrato));
    if (valores.status) parametros.set('status', valores.status);
    if (valores.criadoDe) parametros.set('de', valores.criadoDe);
    if (valores.criadoAte) parametros.set('ate', valores.criadoAte);
    if (pagina > 1) parametros.set('pagina', String(pagina));
    if (tamanhoPagina !== 20) parametros.set('tamanho', String(tamanhoPagina));
    history.replace({ pathname: location.pathname, search: parametros.toString() });
  };

  useEffect(() => {
    buscar(pageAtual, sizeAtual);
    verificarTesteExtracaoMinutaDisponivel().then((resp: any) => {
      const data = resp && resp.data ? resp.data : resp;
      setTesteDisponivel(!!(data && data.disponivel));
    });
    verificarTestePqDisponivel().then((resp: any) => {
      const data = resp && resp.data ? resp.data : resp;
      setTestePqDisponivel(!!(data && data.disponivel));
    });
    verificarHandoverTesteDisponivel().then((resp: any) => {
      const data = resp && resp.data ? resp.data : resp;
      setHandoverTesteDisponivel(!!(data && data.disponivel));
    });
  }, []);

  const executarTesteHandover = () => {
    if (!handoverTesteContrato.trim() || !handoverTesteMinuta || !handoverTestePq) {
      notification.warning('Informe o contrato e selecione a minuta e a PQ.');
      return;
    }
    setHandoverTesteEnviando(true);
    setHandoverTesteResultado(null);
    executarHandoverTeste(
      handoverTesteContrato,
      handoverTesteMinuta,
      handoverTestePq,
      handoverTesteMultiplosGestores,
      handoverTesteFaturamentoDireto,
      handoverTesteAdiantamentoFinanceiro,
    )
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        setHandoverTesteResultado(data || null);
        if (data && data.sucesso) {
          notification.success('Handover de teste enfileirado com sucesso.');
          setHandoverTesteVisivel(false);
          buscar(1, sizeAtual);
        } else {
          notification.error((data && data.mensagem) || 'Não foi possível executar o handover.');
        }
      })
      .catch(() => notification.error('Não foi possível executar o handover.'))
      .finally(() => setHandoverTesteEnviando(false));
  };

  const limparTeste = () => {
    setTesteMinuta(null);
    setTesteResultado(null);
  };

  const fecharTeste = () => {
    if (testeEnviando) return;
    setTesteVisivel(false);
    limparTeste();
  };

  const iniciarTeste = () => {
    if (!testeMinuta) {
      notification.warning('Selecione o PDF da minuta.');
      return;
    }

    setTesteEnviando(true);
    setTesteResultado(null);
    testarExtracaoMinuta(testeMinuta)
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        setTesteResultado(data || null);
        if (!data || !data.sucesso) {
          notification.error((data && data.mensagem) || 'Não foi possível extrair a minuta.');
          return;
        }

        notification.success(data.mensagem || 'Extração da minuta concluída.');
      })
      .catch(() => notification.error('Não foi possível extrair a minuta.'))
      .finally(() => setTesteEnviando(false));
  };

  const limparTestePq = () => {
    setTestePqArquivo(null);
    setTestePqValorTotal('');
    setTestePqResultado(null);
  };

  const fecharTestePq = () => {
    if (testePqEnviando) return;
    setTestePqVisivel(false);
    limparTestePq();
  };

  const iniciarTestePq = () => {
    if (!testePqArquivo) {
      notification.warning('Selecione o arquivo XLSX da PQ.');
      return;
    }
    if (!testePqValorTotal.trim()) {
      notification.warning('Informe o valor total do contrato.');
      return;
    }

    setTestePqEnviando(true);
    setTestePqResultado(null);
    testarValidacaoPq(testePqArquivo, testePqValorTotal)
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        setTestePqResultado(data || null);
        if (!data || !data.sucesso) {
          notification.error((data && data.mensagem) || 'Não foi possível validar a PQ.');
        } else if (!data.valido) {
          notification.warning(data.mensagem || 'A PQ possui erros de validação.');
        } else {
          notification.success(data.mensagem || 'PQ validada com sucesso.');
        }
      })
      .catch(() => notification.error('Não foi possível validar a PQ.'))
      .finally(() => setTestePqEnviando(false));
  };

  const handleFiltrar = () => {
    setPageAtual(1);
    atualizarUrl(1, sizeAtual);
    buscar(1, sizeAtual);
  };

  const handleLimpar = () => {
    setFiltroContrato(undefined);
    setFiltroStatus(undefined);
    setFiltroCriadoDe(undefined);
    setFiltroCriadoAte(undefined);
    setPageAtual(1);
    atualizarUrl(1, sizeAtual, {});
    setLoading(true);
    consultarAtivacoesContrato({ pagina: 1, tamanhoPagina: sizeAtual })
      .then((resp: any) => {
        const data = resp && resp.data ? resp.data : resp;
        if (data) setPaginacao(data);
      })
      .finally(() => setLoading(false));
  };

  const handleTableChange = (pagina: any) => {
    setPageAtual(pagina.current);
    setSizeAtual(pagina.pageSize);
    atualizarUrl(pagina.current, pagina.pageSize);
    buscar(pagina.current, pagina.pageSize);
  };

  const columns = [
    {
      title: 'Nº do contrato',
      dataIndex: 'contratoNumero',
      key: 'contratoNumero',
      width: 125,
      render: (valor: number | null) => <span>{valor == null ? '-' : valor}</span>,
    },
    {
      title: 'Nº da OC',
      dataIndex: 'ordemCompraNumero',
      key: 'ordemCompraNumero',
      width: 115,
      render: (valor: number | null) => <span>{valor == null ? '-' : valor}</span>,
    },
    {
      title: 'Criado em',
      dataIndex: 'criadoEm',
      key: 'criadoEm',
      width: 155,
      render: formatarData,
    },
    {
      title: 'Handover por',
      dataIndex: 'handoverConfirmadoPor',
      key: 'handoverConfirmadoPor',
      width: 190,
      render: (valor: string | null) => (
        <div
          title={valor || undefined}
          style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {valor || '-'}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status: string) => <span>{statusExecucaoLabel(status)}</span>,
    },
    {
      title: 'Administrador',
      dataIndex: 'administradorContrato',
      key: 'administradorContrato',
      width: 190,
      render: (valor: string | null) => (
        <div
          title={valor || undefined}
          style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {valor || 'Pendente de atribuição'}
        </div>
      ),
    },
    {
      title: 'Ativado por',
      dataIndex: 'contratoAtivadoPor',
      key: 'contratoAtivadoPor',
      width: 190,
      render: (valor: string | null) => (
        <div
          title={ativadoPorLabel(valor)}
          style={{ maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ativadoPorLabel(valor)}
        </div>
      ),
    },
    {
      title: 'Resumo',
      dataIndex: 'resumo',
      key: 'resumo',
      width: 360,
      render: (valor: string | null) => (
        <div
          title={valor || undefined}
          style={{ maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {valor || '-'}
        </div>
      ),
    },
    {
      title: 'Processo',
      key: 'detalhe',
      width: 130,
      render: (_: any, item: AtivacaoContratoResumo) => (
        <Link
          className="ativacao-table-link"
          to={{
            pathname: '/ativacaoContratos/' + item.id,
            state: { retorno: location.pathname + location.search },
          }}
          aria-label={'Ver processo do contrato ' + (item.contratoNumero == null ? 'sem número' : item.contratoNumero)}>
          Ver processo <span aria-hidden="true">→</span>
        </Link>
      ),
    },
  ];

  const items = paginacao ? paginacao.items : [];

  return (
    <CompositeView>
      <SimpleView>
        <div className="ativacao-page">
          <GridView onSubmit={handleFiltrar}>
            {(testeDisponivel || testePqDisponivel || handoverTesteDisponivel) && (
              <Widget title="Testes dos documentos da ativação">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                    flexWrap: 'wrap',
                  }}>
                  <div className="ativacao-support-copy">
                    Valide a minuta ou a PQ com as mesmas regras da ativação automática, sem salvar arquivos e sem
                    alterar o contrato.
                  </div>
                  <div className="ativacao-actions">
                    {handoverTesteDisponivel && (
                      <Button
                        type="primary"
                        onClick={() => {
                          setHandoverTesteMultiplosGestores(false);
                          setHandoverTesteFaturamentoDireto(false);
                          setHandoverTesteVisivel(true);
                        }}>
                        Executar handover de teste
                      </Button>
                    )}
                    {testeDisponivel && (
                      <Button
                        type="primary"
                        onClick={() => {
                          setTesteResultado(null);
                          setTesteVisivel(true);
                        }}>
                        Testar minuta
                      </Button>
                    )}
                    {testePqDisponivel && (
                      <Button
                        onClick={() => {
                          setTestePqResultado(null);
                          setTestePqVisivel(true);
                        }}>
                        Testar PQ
                      </Button>
                    )}
                  </div>
                </div>
              </Widget>
            )}
            <FilterWidget
              active={[]}
              actions={[
                <SearchButton type="primary" key="buscar" onClick={handleFiltrar} />,
                <Button key="limpar" onClick={handleLimpar} style={{ marginLeft: 8 }}>
                  Limpar
                </Button>,
              ]}
              title="Filtros">
              <div className="ativacao-filter-grid">
                <div className="ativacao-field">
                  <label htmlFor="ativacao-contrato">Nº do contrato</label>
                  <Input
                    id="ativacao-contrato"
                    name="contrato"
                    type="number"
                    autoComplete="off"
                    placeholder="Todos"
                    value={filtroContrato}
                    onChange={(evento: any) => {
                      const valor = evento.target.value;
                      const numero = Number(valor);
                      setFiltroContrato(valor === '' || isNaN(numero) ? undefined : numero);
                    }}
                  />
                </div>
                <div className="ativacao-field ativacao-field--periodo">
                  <label htmlFor="ativacao-periodo">Período de criação</label>
                  <DatePicker.RangePicker
                    id="ativacao-periodo"
                    name="periodoCriacao"
                    format="DD/MM/YYYY"
                    placeholder={['Data inicial', 'Data final']}
                    value={
                      filtroCriadoDe && filtroCriadoAte
                        ? [moment(filtroCriadoDe.substring(0, 10)), moment(filtroCriadoAte.substring(0, 10))]
                        : undefined
                    }
                    onChange={(datas: any) => {
                      setFiltroCriadoDe(
                        datas && datas[0] ? datas[0].format('YYYY-MM-DD') + 'T00:00:00' : undefined,
                      );
                      setFiltroCriadoAte(
                        datas && datas[1] ? datas[1].format('YYYY-MM-DD') + 'T23:59:59.999' : undefined,
                      );
                    }}
                  />
                </div>
                <div className="ativacao-field ativacao-field--status">
                  <label htmlFor="ativacao-status">Status</label>
                  <Select
                    id="ativacao-status"
                    allowClear
                    placeholder="Todos"
                    value={filtroStatus}
                    onChange={(valor: AtivacaoContratoStatus) => setFiltroStatus(valor || undefined)}>
                    {STATUS_OPCOES.map(opcao => (
                      <Select.Option key={opcao.value} value={opcao.value}>
                        {opcao.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            </FilterWidget>
            <Widget
              title={
                'Ativações recebidas do handover' + (paginacao ? ' — ' + paginacao.totalItems + ' processos' : '')
              }>
              <span className="ativacao-sr-only" role="status" aria-live="polite">
                {loading && 'Carregando ativações…'}
                {!loading && paginacao && paginacao.totalItems + ' processos encontrados.'}
                {!loading && !paginacao && 'Lista pronta.'}
              </span>
              <Table
                className="no-bold-table ativacao-table"
                columns={columns}
                dataSource={items}
                rowKey="id"
                size="small"
                loading={loading}
                scroll={{ x: 1465 }}
                onChange={handleTableChange}
                pagination={{
                  current: pageAtual,
                  pageSize: sizeAtual,
                  total: paginacao ? paginacao.totalItems : 0,
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50'],
                }}
                locale={{ emptyText: 'Nenhuma execução encontrada.' }}
              />
            </Widget>
          </GridView>
        </div>
      </SimpleView>

      <Modal
        title="Testar extração da minuta"
        visible={testeVisivel}
        onCancel={fecharTeste}
        onOk={iniciarTeste}
        okText="Extrair campos"
        cancelText="Fechar"
        confirmLoading={testeEnviando}
        maskClosable={!testeEnviando}
        closable={!testeEnviando}
        okButtonProps={{ disabled: !testeMinuta }}
        width={1100}>
        <div style={{ marginBottom: 18, color: '#555', lineHeight: 1.5 }}>
          Este teste executa o mesmo extrator usado na ativação automática. O PDF não é salvo nos documentos do contrato
          e nenhuma ativação é criada.
        </div>
        <div
          style={{
            marginBottom: 18,
            padding: 16,
            background: '#fafafa',
            border: '1px solid #e8e8e8',
            borderRadius: 6,
          }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Minuta (PDF)</div>
          <Upload
            accept=".pdf,application/pdf"
            multiple={false}
            disabled={testeEnviando}
            fileList={testeMinuta ? [testeMinuta] : []}
            beforeUpload={(arquivo: any) => {
              setTesteMinuta(arquivo);
              setTesteResultado(null);
              return false;
            }}
            onRemove={() => {
              setTesteMinuta(null);
              setTesteResultado(null);
              return true;
            }}>
            <Button disabled={testeEnviando}>Selecionar minuta</Button>
          </Upload>
          <small style={{ display: 'block', marginTop: 6, color: '#777' }}>
            O nome do arquivo pode ser qualquer um. Apenas o conteúdo do PDF será analisado.
          </small>
        </div>

        {testeEnviando && (
          <div style={{ padding: 32, textAlign: 'center' }} role="status" aria-live="polite">
            <Spin tip="Extraindo os campos da minuta…" />
          </div>
        )}

        {!testeEnviando && testeResultado && (
          <div aria-live="polite">
            <div
              style={{
                marginBottom: 16,
                padding: '12px 16px',
                color: testeResultado.sucesso ? '#237804' : '#a8071a',
                background: testeResultado.sucesso ? '#f6ffed' : '#fff1f0',
                border: '1px solid ' + (testeResultado.sucesso ? '#b7eb8f' : '#ffa39e'),
                borderRadius: 6,
              }}>
              {testeResultado.mensagem}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 10,
                marginBottom: 16,
              }}>
              {[
                {
                  titulo: 'Tipo identificado',
                  valor: testeResultado.tipoMinuta
                    ? testeResultado.tipoMinuta +
                      (testeResultado.confiancaTipoMinuta > 0
                        ? ' (' + Math.round(testeResultado.confiancaTipoMinuta * 100) + '%)'
                        : '')
                    : 'Não identificado',
                },
                {
                  titulo: 'Campos encontrados',
                  valor: String(testeResultado.campos.filter(campo => possuiValor(campo.valor)).length),
                },
                {
                  titulo: 'Não preenchidos',
                  valor: String(testeResultado.campos.filter(campo => !possuiValor(campo.valor)).length),
                },
              ].map(item => (
                <div key={item.titulo} style={{ padding: 12, border: '1px solid #e8e8e8', borderRadius: 6 }}>
                  <small style={{ display: 'block', marginBottom: 4, color: '#777' }}>{item.titulo}</small>
                  <strong style={{ color: '#444', wordBreak: 'break-word' }}>{item.valor}</strong>
                </div>
              ))}
            </div>

            <Table
              className="no-bold-table"
              dataSource={testeResultado.campos}
              rowKey="nome"
              size="small"
              pagination={false}
              scroll={{ x: 900, y: 400 }}
              locale={{ emptyText: 'Nenhum campo aplicável foi identificado.' }}
              columns={[
                {
                  title: 'Campo',
                  dataIndex: 'nome',
                  key: 'nome',
                  width: 230,
                  render: (nome: string) => <strong style={{ color: '#444' }}>{campoLabel(nome)}</strong>,
                },
                {
                  title: 'Valor extraído',
                  dataIndex: 'valor',
                  key: 'valor',
                  render: (valor: string | null) => (
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{valor || '-'}</span>
                  ),
                },
                {
                  title: 'Situação',
                  key: 'situacao',
                  width: 135,
                  render: (_: any, campo: MinutaTesteExtracaoCampo) =>
                    possuiValor(campo.valor) ? (
                      <Tag color="green">Encontrado</Tag>
                    ) : (
                      <Tag color="orange">Não preenchido</Tag>
                    ),
                },
                {
                  title: 'Método',
                  dataIndex: 'metodo',
                  key: 'metodo',
                  width: 170,
                  render: (metodo: string) => metodoExtracaoLabel(metodo),
                },
                {
                  title: 'Confiança',
                  dataIndex: 'confianca',
                  key: 'confianca',
                  width: 100,
                  render: (confianca: number) => Math.round((confianca || 0) * 100) + '%',
                },
              ]}
              style={{ overflow: 'hidden', border: '1px solid #efdbff', borderRadius: 6 }}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="Executar handover de teste"
        visible={handoverTesteVisivel}
        onCancel={() => !handoverTesteEnviando && setHandoverTesteVisivel(false)}
        onOk={executarTesteHandover}
        okText="Executar handover"
        cancelText="Fechar"
        confirmLoading={handoverTesteEnviando}
        okButtonProps={{ disabled: !handoverTesteContrato.trim() || !handoverTesteMinuta || !handoverTestePq }}>
        <div style={{ marginBottom: 16, color: '#555' }}>
          Informe os arquivos e o contrato. Eles serão salvos nos documentos iniciais e a ativação será enfileirada como
          no handover real.
        </div>
        <div className="ativacao-field ativacao-modal-field">
          <label htmlFor="handover-contrato">Número do contrato</label>
          <Input
            id="handover-contrato"
            name="contratoHandover"
            type="number"
            autoComplete="off"
            placeholder="Ex.: 12345"
            value={handoverTesteContrato}
            disabled={handoverTesteEnviando}
            onChange={(evento: any) => setHandoverTesteContrato(evento.target.value)}
          />
        </div>
        <div className="ativacao-upload-grid">
          <Upload
            accept=".pdf,application/pdf"
            multiple={false}
            disabled={handoverTesteEnviando}
            fileList={handoverTesteMinuta ? [handoverTesteMinuta] : []}
            beforeUpload={(arquivo: any) => {
              setHandoverTesteMinuta(arquivo);
              return false;
            }}
            onRemove={() => {
              setHandoverTesteMinuta(null);
              return true;
            }}>
            <Button>Selecionar minuta (PDF)</Button>
          </Upload>
          <Upload
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple={false}
            disabled={handoverTesteEnviando}
            fileList={handoverTestePq ? [handoverTestePq] : []}
            beforeUpload={(arquivo: any) => {
              setHandoverTestePq(arquivo);
              return false;
            }}
            onRemove={() => {
              setHandoverTestePq(null);
              return true;
            }}>
            <Button>Selecionar PQ (XLSX)</Button>
          </Upload>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 20 }}>
          <Checkbox
            checked={handoverTesteMultiplosGestores}
            disabled={handoverTesteEnviando}
            onChange={(evento: any) => setHandoverTesteMultiplosGestores(evento.target.checked)}>
            Contrato com múltiplos gestores
          </Checkbox>
          <Checkbox
            checked={handoverTesteFaturamentoDireto}
            disabled={handoverTesteEnviando}
            onChange={(evento: any) => setHandoverTesteFaturamentoDireto(evento.target.checked)}>
            Contrato com faturamento direto
          </Checkbox>
          <Checkbox
            checked={handoverTesteAdiantamentoFinanceiro}
            disabled={handoverTesteEnviando}
            onChange={(evento: any) => setHandoverTesteAdiantamentoFinanceiro(evento.target.checked)}>
            Contrato com adiantamento financeiro
          </Checkbox>
        </div>
        {handoverTesteResultado && !handoverTesteResultado.sucesso && (
          <div className="ativacao-inline-error" role="alert">
            {handoverTesteResultado.mensagem} Revise os arquivos e tente novamente.
          </div>
        )}
      </Modal>

      <Modal
        title="Testar validação da PQ"
        visible={testePqVisivel}
        onCancel={fecharTestePq}
        onOk={iniciarTestePq}
        okText="Validar PQ"
        cancelText="Fechar"
        confirmLoading={testePqEnviando}
        maskClosable={!testePqEnviando}
        closable={!testePqEnviando}
        okButtonProps={{ disabled: !testePqArquivo || !testePqValorTotal.trim() }}
        width={1000}>
        <div style={{ marginBottom: 18, color: '#555', lineHeight: 1.5 }}>
          Este teste executa o mesmo leitor e as mesmas validações de estrutura, itens e valores usados na ativação
          automática. A PQ não é importada e nenhuma alteração é feita no contrato.
        </div>

        <div
          className="ativacao-test-grid"
          style={{
            display: 'grid',
            gap: 16,
            marginBottom: 18,
            padding: 16,
            background: '#fafafa',
            border: '1px solid #e8e8e8',
            borderRadius: 6,
          }}>
          <div>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>PQ (XLSX)</div>
            <Upload
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              multiple={false}
              disabled={testePqEnviando}
              fileList={testePqArquivo ? [testePqArquivo] : []}
              beforeUpload={(arquivo: any) => {
                setTestePqArquivo(arquivo);
                setTestePqResultado(null);
                return false;
              }}
              onRemove={() => {
                setTestePqArquivo(null);
                setTestePqResultado(null);
                return true;
              }}>
              <Button disabled={testePqEnviando}>Selecionar PQ</Button>
            </Upload>
          </div>
          <div>
            <label className="ativacao-modal-label" htmlFor="teste-pq-valor-total">
              Valor total do contrato
            </label>
            <Input
              id="teste-pq-valor-total"
              name="valorTotalContrato"
              inputMode="decimal"
              autoComplete="off"
              value={testePqValorTotal}
              disabled={testePqEnviando}
              placeholder="Ex.: 1.250.000,00"
              onChange={(evento: any) => {
                setTestePqValorTotal(evento.target.value);
                setTestePqResultado(null);
              }}
            />
            <small style={{ display: 'block', marginTop: 6, color: '#777' }}>
              Esse valor será comparado com o totalizador SOMA da PQ.
            </small>
          </div>
        </div>

        {testePqEnviando && (
          <div style={{ padding: 32, textAlign: 'center' }} role="status" aria-live="polite">
            <Spin tip="Validando a PQ…" />
          </div>
        )}

        {!testePqEnviando && testePqResultado && (
          <div aria-live="polite">
            <div
              style={{
                marginBottom: 16,
                padding: '12px 16px',
                color: testePqResultado.valido ? '#237804' : '#a8071a',
                background: testePqResultado.valido ? '#f6ffed' : '#fff1f0',
                border: '1px solid ' + (testePqResultado.valido ? '#b7eb8f' : '#ffa39e'),
                borderRadius: 6,
              }}>
              {testePqResultado.mensagem}
            </div>

            {testePqResultado.sucesso && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                  gap: 10,
                  marginBottom: 16,
                }}>
                {[
                  {
                    titulo: 'Situação',
                    valor: testePqResultado.valido ? 'PQ válida' : 'PQ inválida',
                  },
                  {
                    titulo: 'Valor do contrato',
                    valor: formatarMoeda(testePqResultado.valorTotalContrato),
                  },
                  {
                    titulo: 'Total da PQ',
                    valor: formatarMoeda(testePqResultado.valorTotalPq),
                  },
                  {
                    titulo: 'Linhas processadas',
                    valor: String(testePqResultado.quantidadeLinhas),
                  },
                ].map(item => (
                  <div key={item.titulo} style={{ padding: 12, border: '1px solid #e8e8e8', borderRadius: 6 }}>
                    <small style={{ display: 'block', marginBottom: 4, color: '#777' }}>{item.titulo}</small>
                    <strong style={{ color: '#444', wordBreak: 'break-word' }}>{item.valor}</strong>
                  </div>
                ))}
              </div>
            )}

            {testePqResultado.sucesso && (
              <Table
                className="no-bold-table"
                dataSource={obterValidacoesTestePq(testePqResultado)}
                rowKey={(erro: any) => erro.codigo + '-' + String(erro.linha) + '-' + erro.mensagem}
                size="small"
                pagination={false}
                scroll={{ x: 750, y: 360 }}
                locale={{ emptyText: 'Nenhum erro encontrado. A PQ está válida.' }}
                columns={[
                  {
                    title: 'Código',
                    dataIndex: 'codigo',
                    key: 'codigo',
                    width: 260,
                  },
                  {
                    title: 'Linha',
                    dataIndex: 'linha',
                    key: 'linha',
                    width: 90,
                    render: (linha: number | null) => (linha == null ? '-' : linha),
                  },
                  {
                    title: 'Validação',
                    dataIndex: 'mensagem',
                    key: 'mensagem',
                    className: 'ativacao-align-left',
                    render: (mensagem: string) => (
                      <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{mensagem}</span>
                    ),
                  },
                ]}
                style={{ overflow: 'hidden', border: '1px solid #efdbff', borderRadius: 6 }}
              />
            )}
          </div>
        )}
      </Modal>
    </CompositeView>
  );
};

export default AtivacaoContrato;
